import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";

/**
 * A tiny live-visits badge. Polls /website/projects/{id}/live every 8s.
 * Renders a subtle pulse-dot when a visitor was active in the last 30 seconds.
 *
 * Props:
 *  - blueprintId: string (required)
 *  - saved: bool     — must be truthy to poll (unsaved sites can't have hits)
 *  - variant: "chip" (default) | "dot"
 */
export default function LiveBadge({ blueprintId, saved, variant = "chip" }) {
  const [state, setState] = useState({ active: false, last_60s: 0 });
  const timerRef = useRef(null);

  useEffect(() => {
    if (!blueprintId || !saved) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/website/projects/${blueprintId}/live`);
        if (!cancelled) setState({ active: !!data.active, last_60s: data.last_60s || 0 });
      } catch { /* silent — badge is a nice-to-have */ }
    };
    tick();
    timerRef.current = setInterval(tick, 8000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [blueprintId, saved]);

  if (!blueprintId || !saved) return null;

  if (variant === "dot") {
    return (
      <span
        className={`inline-block w-2 h-2 rounded-full ${state.active ? "bg-teal animate-pulse" : "bg-ink2/30"}`}
        title={state.active ? `${state.last_60s} visits in the last minute` : "Quiet right now"}
        data-testid={`live-dot-${blueprintId}`}
        aria-label={state.active ? "Live visitors right now" : "Quiet"}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border ${
        state.active
          ? "bg-teal/15 text-teal border-teal/40"
          : "bg-ink/5 text-ink2 border-ink/10"
      }`}
      title={state.active ? `${state.last_60s} visits in the last minute` : "No visitors in the last 30 seconds"}
      data-testid={`live-badge-${blueprintId}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${state.active ? "bg-teal animate-pulse" : "bg-ink2/40"}`} />
      {state.active ? `Live · ${state.last_60s}/min` : "Quiet"}
    </span>
  );
}
